<template>
  <div class="rp-root">
  <head>
    <meta charset="utf-8" /><title>FLOCKR | Reset Password</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <link rel="shortcut icon" href="assets/images/favicon.svg">
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <link href="assets/css/custom.css" rel="stylesheet" type="text/css" />
  </head>
  <body data-sidebar="dark">
    <div class="rp-wrapper">
      <div class="rp-card">
        <!-- Logo -->
        <div class="text-center mb-4">
          <img src="assets/images/flockr_logo.png" alt="Flockr" style="height:44px;cursor:pointer;" @click="$router.push('/')" />
        </div>

        <!-- ===== STEP 1: ENTER PHONE ===== -->
        <div v-if="step === 1">
          <div class="text-center mb-4">
            <div style="width:64px;height:64px;border-radius:50%;background:rgba(191,161,100,.1);display:inline-flex;align-items:center;justify-content:center;margin-bottom:12px;">
              <i class="mdi mdi-lock-reset" style="font-size:28px;color:#BFA164;"></i>
            </div>
            <h5 class="fw-bold" style="color:var(--fm-h,#F5F0E8);">Reset Password</h5>
            <p style="color:var(--fm-s,#6B6356);font-size:13px;">Enter your phone number to receive a reset code.</p>
          </div>

          <div class="mb-3">
            <label class="auth-label">Phone Number</label>
            <div class="input-group">
              <select v-model="countryCode" class="form-select" style="background:var(--fm-bg3,#1E1E1E);border-color:#2A2520;color:#BFA164;font-weight:600;max-width:110px;font-size:13px;padding:6px 8px;">
                <option v-for="c in countryCodes" :key="c.code" :value="c.code">{{ c.flag }} +{{ c.code }}</option>
              </select>
              <input v-model="phone" type="tel" class="form-control auth-input" placeholder="712 345 678" maxlength="15" @input="autoFixPhone" @keyup.enter="requestReset" style="border-left:none;" />
            </div>
          </div>

          <div v-if="errorMsg" class="alert alert-danger py-2 px-3" style="background:#2D1111;border-color:#5C2020;color:#FF6B6B;font-size:13px;"><i class="mdi mdi-alert-circle me-1"></i> {{ errorMsg }}</div>

          <button class="btn btn-warning w-100 mt-2" @click="requestReset" :disabled="!phone || submitting">
            <span v-if="submitting"><span class="spinner-border spinner-border-sm me-1"></span> Sending...</span>
            <span v-else><i class="mdi mdi-send me-1"></i> Send Reset Code</span>
          </button>

          <div class="text-center mt-4">
            <a href="javascript:void(0)" @click="$router.push('/login')" style="color:var(--fm-s,#6B6356);font-size:13px;text-decoration:none;"><i class="mdi mdi-arrow-left me-1"></i>Back to Login</a>
          </div>
        </div>

        <!-- ===== STEP 2: ENTER OTP + NEW PASSWORD ===== -->
        <div v-if="step === 2">
          <div class="text-center mb-4">
            <h5 class="fw-bold" style="color:var(--fm-h,#F5F0E8);">Enter Reset Code</h5>
            <p style="color:var(--fm-s,#6B6356);font-size:13px;">We sent a 6-digit code to <strong style="color:var(--fm-h,#F5F0E8);">+{{ phoneFormatted }}</strong></p>
          </div>

          <div class="mb-3">
            <label class="auth-label">OTP Code</label>
            <input v-model="otpCode" type="text" class="form-control auth-input text-center" placeholder="000000" maxlength="6" style="font-size:24px;letter-spacing:8px;font-weight:700;" />
          </div>

          <div class="mb-3">
            <label class="auth-label">New Password</label>
            <input :type="showPassword ? 'text' : 'password'" v-model="newPassword" class="form-control auth-input" placeholder="Min 6 characters" />
            <div class="d-flex align-items-center gap-2 mt-1">
              <input type="checkbox" id="showPwd2" v-model="showPassword" style="accent-color:#BFA164;" />
              <label for="showPwd2" style="color:var(--fm-s,#6B6356);font-size:11px;margin:0;">Show password</label>
            </div>
          </div>

          <div v-if="resetError" class="alert alert-danger py-2 px-3" style="background:#2D1111;border-color:#5C2020;color:#FF6B6B;font-size:13px;"><i class="mdi mdi-alert-circle me-1"></i> {{ resetError }}</div>

          <button class="btn btn-warning w-100 mt-2" @click="resetPassword" :disabled="otpCode.length < 6 || newPassword.length < 6 || resetting">
            <span v-if="resetting"><span class="spinner-border spinner-border-sm me-1"></span> Resetting...</span>
            <span v-else><i class="mdi mdi-lock-check me-1"></i> Reset Password</span>
          </button>

          <div class="text-center mt-3">
            <a href="javascript:void(0)" @click="step=1" style="color:var(--fm-s,#6B6356);font-size:12px;text-decoration:none;"><i class="mdi mdi-arrow-left me-1"></i>Change phone number</a>
          </div>
        </div>

        <!-- ===== STEP 3: SUCCESS ===== -->
        <div v-if="step === 3">
          <div class="text-center">
            <div style="width:64px;height:64px;border-radius:50%;background:rgba(80,200,120,.1);display:inline-flex;align-items:center;justify-content:center;margin-bottom:12px;">
              <i class="mdi mdi-check-circle" style="font-size:32px;color:#50C878;"></i>
            </div>
            <h5 class="fw-bold" style="color:var(--fm-h,#F5F0E8);">Password Reset!</h5>
            <p style="color:var(--fm-m,#A09888);font-size:13px;">Your password has been updated successfully.</p>
            <button class="btn btn-warning w-100 mt-3" @click="$router.push('/login')">
              <i class="mdi mdi-login me-1"></i> Sign In Now
            </button>
          </div>
        </div>
      </div>
    </div>
  </body>
  </div>
</template>
<style>

/* === GLOBAL THEME === */
html[data-theme="light"] body { background:#FFFFFF!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-card,html[data-theme="light"] .p2c,html[data-theme="light"] .ld-section,html[data-theme="light"] .ld-action-card,html[data-theme="light"] .ld-seller-card,html[data-theme="light"] .ld-info-card,html[data-theme="light"] .ld-related-card { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-overlay .fm-modal,html[data-theme="light"] .ad-overlay .ad-modal { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-input,html[data-theme="light"] .fm-select { background:#F7F5F2!important; border-color:#E0D8D0!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-label { color:#555!important; }
html[data-theme="dark"] body { background:#0A0A0A!important; color:#F5F0E8!important; }
</style>
<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .card-body{color:#1A1A1A!important}
html[data-theme="light"] .modal-content{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#1A1A1A!important}
html[data-theme="light"] .form-control,html[data-theme="light"] .form-select{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .table{color:#1A1A1A!important}
html[data-theme="light"] .table th{color:#555!important;border-color:#E8E2DA!important}
html[data-theme="light"] .table td{border-color:#F0EBE4!important}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:#777!important}
html[data-theme="light"] [style*="color:#A09888"]{color:#555!important}
html[data-theme="light"] [style*="background:#0F0F0F"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#141414"]{background:#FFFFFF!important}
html[data-theme="light"] [style*="background:#1E1E1E"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="background:#0A0A0A"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#1a1510"]{background:#FFF8F0!important}
html[data-theme="light"] [style*="background:#1A1A1A"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="border:1px solid #1E1E1E"]{border-color:#E8E2DA!important}
html[data-theme="light"] [style*="border:1px solid #2A2520"]{border-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1A1A1A"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1E1E1E"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #2A2520"]{border-bottom-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-top:1px solid #2A2520"]{border-top-color:#E0D8D0!important}
html[data-theme="light"] [style*="color:#BFA164"]{color:#BFA164!important}
html[data-theme="light"] [style*="color:#50C878"]{color:#50C878!important}
html[data-theme="light"] [style*="color:#4FC3F7"]{color:#4FC3F7!important}
html[data-theme="light"] [style*="color:#FF6B6B"]{color:#FF6B6B!important}
html[data-theme="light"] [style*="color:#FF9800"]{color:#FF9800!important}
html[data-theme="light"] [style*="color:#CE93D8"]{color:#CE93D8!important}
</style>
<style scoped>
.rp-root{background:#0A0A0A!important;min-height:100vh}
.rp-wrapper{display:flex;align-items:center;justify-content:center;min-height:100vh;padding:20px}
.rp-card{background:var(--fm-bg1,#141414)!important;border:1px solid var(--fm-br,#1E1E1E);border-radius:16px;padding:40px 32px;width:100%;max-width:420px}
.auth-label{font-size:13px;font-weight:600;color:#D4C9B8!important;margin-bottom:6px;display:block}
.auth-input{background:var(--fm-bg2,#0F0F0F)!important;border:1px solid var(--fm-br,#2A2520);color:var(--fm-h,#F5F0E8)!important;border-radius:8px}
.auth-input:focus{border-color:#BFA164;background:var(--fm-bg2,#0F0F0F)!important;color:var(--fm-h,#F5F0E8)!important;box-shadow:0 0 0 2px rgba(191,161,100,.15)}
.auth-input::placeholder{color:#4A4540!important}
@media(max-width:480px){.rp-card{padding:28px 20px;border-radius:12px}}
</style>
<script>
import { executeJson, flocker } from "@/api";
export default {
  name: "ResetPasswordPage",
  data(){return{
      darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
step:1,phone:'',otpCode:'',newPassword:'',showPassword:false,submitting:false,resetting:false,errorMsg:'',resetError:'',countryCode:'254',
    countryCodes:[
      {code:'254',name:'Kenya',flag:'\ud83c\uddf0\ud83c\uddea'},{code:'256',name:'Uganda',flag:'\ud83c\uddfa\ud83c\uddec'},{code:'255',name:'Tanzania',flag:'\ud83c\uddf9\ud83c\uddff'},
      {code:'250',name:'Rwanda',flag:'\ud83c\uddf7\ud83c\uddfc'},{code:'257',name:'Burundi',flag:'\ud83c\udde7\ud83c\uddee'},{code:'251',name:'Ethiopia',flag:'\ud83c\uddea\ud83c\uddf9'},
      {code:'252',name:'Somalia',flag:'\ud83c\uddf8\ud83c\uddf4'},{code:'211',name:'South Sudan',flag:'\ud83c\uddf8\ud83c\uddf8'},{code:'243',name:'DR Congo',flag:'\ud83c\udde8\ud83c\udde9'},
      {code:'234',name:'Nigeria',flag:'\ud83c\uddf3\ud83c\uddec'},{code:'233',name:'Ghana',flag:'\ud83c\uddec\ud83c\udded'},{code:'27',name:'South Africa',flag:'\ud83c\uddff\ud83c\udde6'},
      {code:'260',name:'Zambia',flag:'\ud83c\uddff\ud83c\uddf2'},{code:'263',name:'Zimbabwe',flag:'\ud83c\uddff\ud83c\uddfc'},{code:'265',name:'Malawi',flag:'\ud83c\uddf2\ud83c\uddfc'},
      {code:'258',name:'Mozambique',flag:'\ud83c\uddf2\ud83c\uddff'},{code:'249',name:'Sudan',flag:'\ud83c\uddf8\ud83c\udde9'},{code:'20',name:'Egypt',flag:'\ud83c\uddea\ud83c\uddec'},
      {code:'212',name:'Morocco',flag:'\ud83c\uddf2\ud83c\udde6'},{code:'237',name:'Cameroon',flag:'\ud83c\udde8\ud83c\uddf2'},
      {code:'44',name:'United Kingdom',flag:'\ud83c\uddec\ud83c\udde7'},{code:'1',name:'USA/Canada',flag:'\ud83c\uddfa\ud83c\uddf8'},
      {code:'91',name:'India',flag:'\ud83c\uddee\ud83c\uddf3'},{code:'86',name:'China',flag:'\ud83c\udde8\ud83c\uddf3'},{code:'971',name:'UAE',flag:'\ud83c\udde6\ud83c\uddea'},
      {code:'966',name:'Saudi Arabia',flag:'\ud83c\uddf8\ud83c\udde6'},{code:'49',name:'Germany',flag:'\ud83c\udde9\ud83c\uddea'},{code:'33',name:'France',flag:'\ud83c\uddeb\ud83c\uddf7'}
    ]
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    themeCardStyle(){return this.darkMode?'background:#141414!important;border:1px solid #1E1E1E!important;':'background:#FFFFFF!important;border:1px solid #E8E2DA!important;'},
    themeInputStyle(){return this.darkMode?'background:#0F0F0F;border:1px solid #2A2520;color:#F5F0E8;':'background:#FFFFFF;border:1px solid #E0D8D0;color:#1A1A1A;'},

    phoneFormatted(){var p=(this.phone||'').replace(/[^0-9]/g,'');if(p.startsWith('0'))p=p.substring(1);if(p.startsWith(this.countryCode))p=p.substring(this.countryCode.length);return p?this.countryCode+p:''}
  },
  mounted(){var t=localStorage.getItem('flockr_theme')||'dark';window.__flockrSetTheme(t);},
  methods:{
    autoFixPhone(){
      var p=this.phone.replace(/[^0-9]/g,'');
      if(p.startsWith('0'))p=p.substring(1);
      if(p.startsWith(this.countryCode))p=p.substring(this.countryCode.length);
      this.phone=p;
    },
    formatPhone(raw){
      var p=(raw||'').replace(/[^0-9]/g,'');
      if(p.startsWith('0'))p=p.substring(1);
      if(p.startsWith(this.countryCode))p=p.substring(this.countryCode.length);
      return p?this.countryCode+p:'';
    },
    async requestReset(){
      this.submitting=true;this.errorMsg='';
      try{
        var ph=this.formatPhone(this.phone);
        if(!ph){this.errorMsg='Please enter a valid phone number';this.submitting=false;return}
        var r=await executeJson({function:"requestPasswordReset",phone:ph},flocker);
        if(r.data.success){this.step=2}
        else{this.errorMsg=r.data.message||'Phone not found. Please check and try again.'}
      }catch(e){console.error(e);this.errorMsg='Connection error. Please try again.'}
      this.submitting=false
    },
    async resetPassword(){
      this.resetting=true;this.resetError='';
      try{
        var ph=this.formatPhone(this.phone);
        var r=await executeJson({function:"resetPassword",phone:ph,code:this.otpCode,newPassword:this.newPassword},flocker);
        if(r.data.success){this.step=3}
        else{this.resetError=r.data.message||'Invalid or expired code. Please try again.'}
      }catch(e){console.error(e);this.resetError='Connection error. Please try again.'}
      this.resetting=false
    }
  }
};
</script>
